import React, { useEffect, useState, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { formatRupee, formatQty } from '../utils/format';
import { transactionsApi } from '../utils/api';
import AddStockModal from '../components/AddStockModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';

export default function Dashboard({ navigate }) {
  const { stocks, loading, error, fetchStocks, deleteStock } = useApp();
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('stockName');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showAddStock, setShowAddStock] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => {
    fetchStocks({ search, sortBy, order: sortOrder });
  }, [fetchStocks, search, sortBy, sortOrder]);

  useEffect(() => { load(); }, [load]);

  const handleSort = (col) => {
    if (sortBy === col) setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortOrder('asc'); }
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <span style={{ color: 'var(--text-dim)' }}> ↕</span>;
    return <span style={{ color: 'var(--accent)' }}>{sortOrder === 'asc' ? ' ↑' : ' ↓'}</span>;
  };

  const totals = stocks.reduce((acc, s) => ({
    investment: acc.investment + (s.totalInvestment || 0),
    stocks: acc.stocks + 1
  }), { investment: 0, stocks: 0 });

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteStock(deleteTarget._id);
      setDeleteTarget(null);
    } catch (e) {
      alert(e.response?.data?.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const exportCSV = () => {
    const rows = [
      ['Stock', 'Total Qty', 'Avg Buy Price (₹)', 'Total Investment (₹)', 'Transactions'],
      ...stocks.map(s => [
        s.stockName,
        s.totalQty,
        s.avgBuyPrice,
        s.totalInvestment,
        s.transactionCount
      ])
    ];
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'portfolio.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="page-header">
        <h2>Portfolio Dashboard</h2>
        <p>Overview of all your stock holdings</p>
      </div>

      {/* Summary Stats */}
      <div className="stat-grid">
        <div className="stat-card">
          <div className="stat-label">Total Stocks</div>
          <div className="stat-value">{totals.stocks}</div>
          <div className="stat-sub">unique holdings</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Investment</div>
          <div className="stat-value" style={{ fontSize: '1.2rem' }}>{formatRupee(totals.investment)}</div>
          <div className="stat-sub">across all positions</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Avg per Stock</div>
          <div className="stat-value" style={{ fontSize: '1.2rem' }}>
            {totals.stocks > 0 ? formatRupee(totals.investment / totals.stocks) : '₹0'}
          </div>
          <div className="stat-sub">average investment</div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="toolbar">
        <div className="search-wrap">
          <span className="search-icon">⌕</span>
          <input
            type="text"
            placeholder="Search stocks…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <button className="btn btn-outline" onClick={exportCSV}>
          ↓ Export CSV
        </button>
        <button className="btn btn-primary" onClick={() => setShowAddStock(true)}>
          + Add Stock
        </button>
        <button className="btn btn-primary" onClick={() => navigate('add')}>
          + Add Transaction
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Table */}
      <div className="table-wrapper">
        {loading ? (
          <div className="loading-wrap"><div className="spinner" /></div>
        ) : stocks.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📊</div>
            <h3>No stocks yet</h3>
            <p>Add your first stock to start tracking your portfolio.</p>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th onClick={() => handleSort('stockName')}>Stock <SortIcon col="stockName" /></th>
                <th onClick={() => handleSort('totalQty')}>Qty <SortIcon col="totalQty" /></th>
                <th onClick={() => handleSort('avgBuyPrice')}>Avg Buy Price <SortIcon col="avgBuyPrice" /></th>
                <th onClick={() => handleSort('totalInvestment')}>Total Investment <SortIcon col="totalInvestment" /></th>
                <th>Entries</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {stocks.map(stock => (
                <tr key={stock._id}>
                  <td>
                    <div className="stock-name-cell">
                      <div className="stock-avatar">
                        {stock.stockName.slice(0, 3)}
                      </div>
                      <div>
                        <strong>{stock.stockName}</strong>
                        {stock.ticker && <small>{stock.ticker}</small>}
                      </div>
                    </div>
                  </td>
                  <td className="mono">{formatQty(stock.totalQty)}</td>
                  <td className="mono">{formatRupee(stock.avgBuyPrice)}</td>
                  <td className="mono text-accent">{formatRupee(stock.totalInvestment)}</td>
                  <td>
                    <span className="badge badge-neutral">{stock.transactionCount}</span>
                  </td>
                  <td>
                    <div className="action-btns">
                      <button
                        className="btn btn-outline btn-sm"
                        onClick={() => navigate('ledger')}
                        title="View ledger"
                      >
                        ≡ Ledger
                      </button>
                      <button
                        className="btn btn-danger btn-sm btn-icon"
                        onClick={() => setDeleteTarget(stock)}
                        title="Delete stock"
                      >
                        ✕
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showAddStock && (
        <AddStockModal
          onClose={() => setShowAddStock(false)}
          onSuccess={() => { setShowAddStock(false); load(); }}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          title={`Delete ${deleteTarget.stockName}?`}
          message={`This will permanently delete "${deleteTarget.stockName}" and all its ${deleteTarget.transactionCount} transaction(s). This cannot be undone.`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
          loading={deleting}
        />
      )}
    </div>
  );
}
